<template>
    <div class="pathogenesis-analysis">
        <div class="chart-row">
            <div class="chart-card w-1-2">
                <p class="chart-card-tit">{{ stackedTitle }}</p>
                <disease-chart :option="stackedOption" height="320px" />
            </div>
            <div class="chart-card w-1-2">
                <p class="chart-card-tit">{{ overviewTitle }}</p>
                <disease-chart :option="overviewOption" height="320px" />
            </div>
        </div>
        <div class="chart-row">
            <div
                v-for="(item, index) in detailCharts"
                :key="item.title + index"
                class="chart-card w-1-4"
            >
                <p class="chart-card-tit">{{ item.title }}</p>
                <disease-chart :option="item.option" height="260px" />
            </div>
        </div>
    </div>
</template>

<script>
import DiseaseChart from './disease_chart.vue';
import pathogenesisAnalysisData from '../mock/pathogenesisAnalysis.json';

const STACK_COLORS = ['#448EF6', '#1EB273', '#F39C12', '#8E44AD', '#E91E63', '#00BCD4', '#F44336'];
const DETAIL_COLORS = ['#db2777', '#7c3aed', '#ea580c', '#165DFF'];

function buildStackedOption(stacked) {
    const xAxis = stacked.xAxis || [];
    const seriesList = stacked.series || [];
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        legend: {
            bottom: 0,
            data: seriesList.map((item) => item.name),
            textStyle: { color: '#5A6779', fontSize: 11 },
        },
        grid: {
            left: 16,
            right: 16,
            top: 16,
            bottom: 36,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: xAxis,
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                interval: 0,
                rotate: 20,
            },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: { color: '#5A6779' },
        },
        series: seriesList.map((item, index) => ({
            name: item.name,
            type: 'bar',
            stack: 'total',
            barWidth: 28,
            emphasis: { focus: 'series' },
            itemStyle: {
                color: STACK_COLORS[index % STACK_COLORS.length],
            },
            data: item.data || [],
        })),
    };
}

function buildColumnOption(list, color) {
    const data = list || [];
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        grid: {
            left: 16,
            right: 16,
            top: 24,
            bottom: 8,
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            data: data.map((item) => item.name),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                interval: 0,
                rotate: 20,
            },
        },
        yAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: { color: '#5A6779' },
        },
        series: [
            {
                type: 'bar',
                data: data.map((item) => item.value),
                barWidth: 22,
                itemStyle: {
                    color,
                    borderRadius: [6, 6, 0, 0],
                },
                label: {
                    show: true,
                    position: 'top',
                    color: '#5A6779',
                    fontSize: 11,
                },
            },
        ],
    };
}

function buildBarOption(list, color) {
    const data = [...(list || [])].reverse();
    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
        },
        grid: {
            left: 16,
            right: 40,
            top: 8,
            bottom: 8,
            containLabel: true,
        },
        xAxis: {
            type: 'value',
            splitLine: { lineStyle: { type: 'dashed', color: '#E8EEF8' } },
            axisLabel: {
                color: '#5A6779',
                hideOverlap: true,
                margin: 10,
            },
        },
        yAxis: {
            type: 'category',
            data: data.map((item) => item.name),
            axisTick: { show: false },
            axisLine: { lineStyle: { color: '#E8EEF8' } },
            axisLabel: {
                color: '#333',
                fontSize: 12,
                margin: 10,
            },
        },
        series: [
            {
                type: 'bar',
                data: data.map((item) => item.value),
                barWidth: 14,
                barCategoryGap: '36%',
                itemStyle: {
                    color,
                    borderRadius: [0, 6, 6, 0],
                },
                label: {
                    show: true,
                    position: 'right',
                    color: '#5A6779',
                    fontSize: 11,
                    distance: 6,
                },
            },
        ],
    };
}

export default {
    name: 'PathogenesisAnalysis',
    components: {
        DiseaseChart,
    },
    data() {
        return {
            chartData: pathogenesisAnalysisData,
        };
    },
    computed: {
        stackedTitle() {
            return (this.chartData.stacked && this.chartData.stacked.title) || '柱状堆积图';
        },
        overviewTitle() {
            return (this.chartData.overview && this.chartData.overview.title) || '类型总览';
        },
        stackedOption() {
            return buildStackedOption(this.chartData.stacked || {});
        },
        overviewOption() {
            const list = (this.chartData.overview && this.chartData.overview.list) || [];
            return buildColumnOption(list, '#165DFF');
        },
        detailCharts() {
            const details = this.chartData.details || [];
            return details.slice(0, 4).map((item, index) => ({
                title: item.title || `类别详情${index + 1}`,
                option: buildBarOption(item.list || [], DETAIL_COLORS[index % DETAIL_COLORS.length]),
            }));
        },
    },
};
</script>

<style lang="less" scoped>
.pathogenesis-analysis {
    .chart-row {
        display: flex;
        gap: 16px;
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .chart-card {
        min-width: 0;
        background: #fff;
        border: 1px solid #E8EEF8;
        border-radius: 10px;
        padding: 12px 14px 16px;
        box-sizing: border-box;

        &.w-1-2 {
            flex: 1 1 0;
        }

        &.w-1-4 {
            flex: 1 1 0;
        }
    }

    .chart-card-tit {
        margin: 0 0 8px;
        font-size: 14px;
        font-weight: bold;
        color: #0C55AA;
        line-height: 20px;
    }
}
</style>
